import { useState, useEffect } from 'react';
import { Zap, Target, TrendingUp, CheckCircle, ArrowRight, Brain, Phone, Mail, MessageCircle, Bot, Layers, Search, FileText, Code, ShieldCheck, Building, ChevronRight, Calendar, Sparkles } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import { Link } from 'react-router-dom';

const BragAI = () => {
  const [isAnimated, setIsAnimated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsAnimated(true);
  }, []);

  const services = [
    {
      icon: <Zap className="w-7 h-7" />,
      tag: "Quick Wins",
      title: "AI Quick Wins",
      description: "Immediate, high-impact AI implementations that show results fast  -  no months-long projects. We identify your biggest time drains and automate them within weeks.",
      color: "blue",
    },
    {
      icon: <Bot className="w-7 h-7" />,
      tag: "AI Engineering",
      title: "AI Agents & Automation",
      description: "Custom AI agents that work 24/7 on your behalf  -  from lead qualification to internal workflows. We design, build, and deploy agents tailored to your business.",
      color: "violet",
    },
    {
      icon: <Search className="w-7 h-7" />,
      tag: "Visibility",
      title: "GEO  -  Get Found by AI",
      description: "Customers now ask ChatGPT and Claude for recommendations instead of just Googling. We optimise your brand to appear when AI answers questions about your industry.",
      color: "emerald",
    },
  ];

  const process = [
    {
      number: "01",
      title: "Discovery Call",
      description: "A 30-minute call to understand your business, workflows, and where AI can create practical value  -  no fluff, no sales pitch.",
      icon: <Phone className="w-6 h-6" />,
    },
    {
      number: "02",
      title: "AI Proposal",
      description: "We deliver a clear, jargon-free proposal outlining the exact automations or agents we recommend, timelines, and expected outcomes.",
      icon: <FileText className="w-6 h-6" />,
    },
    {
      number: "03",
      title: "Implementation",
      description: "We build and deploy your AI solution, train your team on it, and stay available for ongoing support as you scale.",
      icon: <Code className="w-6 h-6" />,
    },
  ];

  const blogPosts = [
    {
      slug: "/blog",
      tag: "Quick Wins",
      title: "5 AI Quick Wins Every Business Can Implement This Week",
      excerpt: "You don't need a six-figure AI strategy to start saving time. Here are five automations that deliver immediate ROI for teams of any size.",
      readTime: "5 min read",
    },
    {
      slug: "/blog",
      tag: "AI Agents",
      title: "What AI Agents Actually Are  -  And Why Your Competitors Are Already Using Them",
      excerpt: "AI agents aren't robots. They're software that can reason, plan, and take actions. Here's what that means for your business in plain English.",
      readTime: "7 min read",
    },
    {
      slug: "/blog",
      tag: "Strategy",
      title: "The Hidden Cost of Not Adopting AI: How Businesses Are Falling Behind",
      excerpt: "While you're evaluating whether AI is worth it, your competitors are already 12 months ahead. The window to catch up is still open  -  but not forever.",
      readTime: "6 min read",
    },
  ];

  const colorMap: Record<string, { bg: string; text: string; border: string; tag: string }> = {
    blue: {
      bg: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-100 hover:border-blue-300",
      tag: "bg-blue-100 text-blue-700",
    },
    violet: {
      bg: "bg-violet-50",
      text: "text-violet-600",
      border: "border-violet-100 hover:border-violet-300",
      tag: "bg-violet-100 text-violet-700",
    },
    emerald: {
      bg: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-100 hover:border-emerald-300",
      tag: "bg-emerald-100 text-emerald-700",
    },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              BragAI
            </a>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Services</a>
              <a href="#process" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">How It Works</a>
              <a href="#about" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">About</a>
              <Link to="/blog" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Blog</Link>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Contact</a>
            </div>
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm"
            >
              Book Discovery Call
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-8 transition-all duration-700 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700 text-sm font-medium">AI Consulting for forward-thinking businesses</span>
            </div>
            <h1 className={`text-5xl md:text-6xl font-bold leading-tight mb-6 transition-all duration-700 delay-100 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              Don't let your business{' '}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                fall behind on AI.
              </span>
            </h1>
            <p className={`text-xl text-gray-600 mb-4 leading-relaxed transition-all duration-700 delay-200 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              We help companies implement AI  -  from quick wins that save hours this week, to custom AI agents that transform how you operate.
            </p>
            <p className={`text-lg text-gray-500 mb-10 transition-all duration-700 delay-300 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              Start with a discovery call. We will show you where AI can help your business save time and reduce costs.
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-400 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-blue-200 hover:shadow-lg"
              >
                Book Discovery Call <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#services"
                className="border border-gray-200 hover:border-gray-300 bg-white text-gray-700 hover:text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                See What We Do
              </a>
            </div>
          </div>

          {/* Stats bar */}
          <div className={`mt-20 grid grid-cols-3 gap-8 max-w-xl transition-all duration-700 delay-500 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div>
              <div className="text-3xl font-bold text-gray-900">Discover</div>
              <div className="text-sm text-gray-500 mt-1">Where AI can benefit your business</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">Save</div>
              <div className="text-sm text-gray-500 mt-1">Time and money with practical AI use</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900">3 steps</div>
              <div className="text-sm text-gray-500 mt-1">From call to live</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">What We Offer</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">AI that works for your business today</h2>
            <p className="text-xl text-gray-500 max-w-2xl">
              Whether you want fast wins or deep transformation, we have an engagement model that fits where you are right now.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const colors = colorMap[service.color];
              return (
                <div key={index} className={`border rounded-2xl p-8 transition-all duration-300 hover:shadow-lg ${colors.border}`}>
                  <div className={`inline-flex p-3 rounded-xl ${colors.bg} mb-5`}>
                    <span className={colors.text}>{service.icon}</span>
                  </div>
                  <div className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mb-4 ${colors.tag}`}>
                    {service.tag}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors shadow-sm"
            >
              Discuss Your Business Needs
            </button>
          </div>
        </div>
      </section>

      {/* Why Act Now Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">The AI Gap Is Real</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Every month you wait, your competitors get further ahead.
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                AI adoption is moving fast. Businesses that are embedding AI into their workflows now are cutting costs, improving quality, and freeing up their teams for higher-value work.
              </p>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-white text-slate-900 hover:bg-gray-100 px-7 py-3.5 rounded-lg font-semibold transition-colors"
              >
                Let's Talk
              </button>
            </div>
            <div className="space-y-4">
              {[
                { icon: <Zap className="w-5 h-5" />, title: "Focus", text: "We identify repetitive tasks where AI can reduce manual effort and improve team focus." },
                { icon: <TrendingUp className="w-5 h-5" />, title: "Efficiency", text: "Automation can help lower operating costs by cutting rework and speeding up routine processes." },
                { icon: <ShieldCheck className="w-5 h-5" />, title: "Consistency", text: "AI agents don't have bad days  -  quality stays high at scale" },
                { icon: <Brain className="w-5 h-5" />, title: "Competitive edge", text: "Early AI adopters in your industry are already building a moat" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-800 rounded-xl p-5">
                  <div className="p-2 bg-blue-600/20 rounded-lg text-blue-400 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-1">{item.title}</div>
                    <div className="text-gray-400 text-sm">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works / Process */}
      <section id="process" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">How It Works</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">From conversation to results in 3 steps</h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              No jargon, no big upfront commitment. We start with a chat and build from there.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-10 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-blue-200 to-violet-200 z-0" />

            {process.map((step, index) => (
              <div key={index} className="relative z-10 text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-100">
                  <span className="text-white text-2xl font-bold">{step.number}</span>
                </div>
                <div className="inline-flex items-center gap-2 text-blue-600 mb-3">
                  {step.icon}
                  <span className="font-semibold">{step.title}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-colors shadow-sm"
            >
              Book Your Discovery Call
            </button>
            <p className="text-gray-400 text-sm mt-3">30 minutes. No obligation. Calendly booking.</p>
          </div>
        </div>
      </section>

      {/* About / Founder Section */}
      <section id="about" className="py-24 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-violet-100 rounded-3xl -z-10" />
              <img
                src="/georgia.jpg"
                alt="Georgia Hasso, founder of BragAI"
                className="w-full max-w-sm mx-auto rounded-2xl shadow-xl object-cover aspect-[3/4]"
              />
            </div>
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Who's Behind BragAI</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Hi, I'm Georgia</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-5">
                I'm an AI consultant helping businesses cut through the hype and actually implement AI that works. I started BragAI because I kept seeing companies either overwhelmed by the options or locked into expensive tools they didn't need.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                My approach is practical and honest. We start with where AI can genuinely help your specific business  -  not a one-size-fits-all package  -  and build from there.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you need a quick win to save your team 10 hours a week, or a custom AI agent to handle an entire workflow, I'll tell you exactly what's worth your time and budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" /> Book a Call with Me
                </button>
                <a
                  href="mailto:bragai.georgia@gmail.com"
                  className="border border-gray-200 hover:border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" /> Send Me an Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GEO Section */}
      <section id="geo" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-violet-50 to-blue-50 rounded-3xl p-10 md:p-14 border border-violet-100">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-violet-100 px-4 py-1.5 rounded-full mb-6">
                <Brain className="w-4 h-4 text-violet-600" />
                <span className="text-violet-700 text-sm font-semibold">Generative Engine Optimization</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Is your business visible to AI  -  or invisible?
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                More and more customers are asking ChatGPT, Claude, and Gemini for recommendations instead of Googling. If your brand isn't optimised for AI, you won't show up  -  even if you rank on Google.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our GEO service makes sure AI systems understand who you are, what you offer, and why you're the right choice  -  so you get recommended when it counts.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  "AI-readable website content",
                  "Entity & brand clarity for LLMs",
                  "FAQ & conversational content",
                  "Schema markup & structured data",
                  "Authority & trust signal building",
                  "Competitor gap analysis",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-violet-600 hover:bg-violet-700 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors shadow-sm"
              >
                Get GEO Optimised
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section id="blog" className="py-24 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Insights</p>
              <h2 className="text-3xl font-bold text-gray-900">AI in the workplace  -  what you need to know</h2>
            </div>
            <Link to="/blog" className="hidden md:flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm">
              View all posts <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Link key={index} to="/blog" className="group bg-white rounded-2xl p-7 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                <div className="inline-flex px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold mb-4">
                  {post.tag}
                </div>
                <h3 className="text-gray-900 font-bold text-lg leading-snug mb-3 group-hover:text-blue-600 transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs">{post.readTime}</span>
                  <ChevronRight className="w-4 h-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/blog" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center justify-center gap-2">
              View all posts <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Get in Touch</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Ready to explore what AI can do for your business?
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed mb-8">
                Start with a 30-minute discovery call. We will talk through your business and identify where AI can save time and reduce costs, with no obligation.
              </p>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-sm flex items-center gap-2"
              >
                <Calendar className="w-5 h-5" /> Book Discovery Call
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-5 border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-0.5">Phone / WhatsApp</div>
                  <a href="tel:+27810915451" className="font-semibold text-gray-900 hover:text-blue-600 transition-colors">+27 81 091 5451</a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-5 border border-gray-100 rounded-xl hover:border-green-200 transition-colors">
                <div className="p-3 bg-green-50 rounded-xl">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-0.5">WhatsApp</div>
                  <a href="https://wa.me/27810915451" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-900 hover:text-green-600 transition-colors">Chat on WhatsApp</a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-5 border border-gray-100 rounded-xl hover:border-violet-200 transition-colors">
                <div className="p-3 bg-violet-50 rounded-xl">
                  <Mail className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-0.5">Email</div>
                  <a href="mailto:bragai.georgia@gmail.com" className="font-semibold text-gray-900 hover:text-violet-600 transition-colors">bragai.georgia@gmail.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">FAQ</p>
            <h2 className="text-3xl font-bold text-gray-900">Common questions</h2>
          </div>
          <div className="space-y-5">
            {[
              {
                q: "What is BragAI?",
                a: "BragAI is an AI consulting business that helps companies implement artificial intelligence  -  from small, immediate productivity wins to fully custom AI agents and workflow automation. We also help businesses become visible in AI-generated search results through Generative Engine Optimization (GEO).",
              },
              {
                q: "What are AI Quick Wins?",
                a: "AI Quick Wins are targeted, fast-to-implement automations that deliver measurable value within weeks  -  not months. Examples include automated email drafting, document summarisation, meeting transcription, or customer query routing. They're a great starting point if you're new to AI.",
              },
              {
                q: "What is an AI agent?",
                a: "An AI agent is software that can reason, plan, and take actions on your behalf  -  connecting to your tools and data to complete multi-step tasks autonomously. We design and build custom agents for workflows like lead qualification, internal knowledge retrieval, reporting, and more.",
              },
              {
                q: "Do I need a big budget or tech team?",
                a: "No. Most of our clients don't have in-house developers or large AI budgets. We work with businesses of all sizes and design solutions that match your current stage. The discovery call is commitment-free.",
              },
              {
                q: "What is GEO and why does it matter?",
                a: "Generative Engine Optimization (GEO) ensures your business appears in answers generated by AI tools like ChatGPT, Claude, and Gemini. As more people ask AI for recommendations instead of Googling, being invisible to AI means losing customers. GEO future-proofs your visibility.",
              },
              {
                q: "How does the process work?",
                a: "We start with a 30-minute discovery call to understand your business. From there, we produce a clear AI proposal  -  no jargon, just specific recommendations and practical next steps. Once approved, we build and deploy your solution and support your team through onboarding.",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent mb-1">
              BragAI
            </div>
            <p className="text-gray-400 text-sm">AI Consulting · Quick Wins · AI Agents · GEO</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a href="#services" className="hover:text-gray-900 transition-colors">Services</a>
            <a href="#process" className="hover:text-gray-900 transition-colors">How It Works</a>
            <Link to="/blog" className="hover:text-gray-900 transition-colors">Blog</Link>
            <a href="#contact" className="hover:text-gray-900 transition-colors">Contact</a>
          </div>
          <p className="text-gray-400 text-sm">© 2025 BragAI. Built for businesses that think forward.</p>
        </div>
      </footer>

      <Analytics />
    </div>
  );
};

export default BragAI;
